import { useMemo } from 'react';
import { tracking } from '@iwoca/frontend-tracking-library';
import { t } from '@lingui/core/macro';
import { useIntroducerData } from 'contexts/IntroducerData/IntroducerData.context';
import { Driver, driver, DriveStep } from 'driver.js';
import { useNavigate } from 'react-router-dom';
import { getAmplitudeDefaultEventProperties } from 'utils/amplitudeEvents';
import { PATH_NAMES } from 'utils/routes/pathNames';
import {
  markTutorialAsComplete,
  useGetIntroducerTutorials,
} from './useTutorials.api';
import {
  addRelaunchStepWhenApplicable,
  closeTourOrJumpToFinalStep,
  renderProgressText,
  renderTranslatedText,
} from './useTutorials.helpers';
import { TTutorialName } from './useTutorials.types';

export const useTutorials = ({
  autoTriggered = true,
}: {
  autoTriggered: boolean;
}) => {
  const navigate = useNavigate();
  const { introducerId } = useIntroducerData();
  const { data: tutorialsData } = useGetIntroducerTutorials(autoTriggered);
  const gotItText = renderTranslatedText('got it');
  const nextText = renderTranslatedText('next');
  const OVERLAY_COLOR = 'rgba(8, 23, 43, 0.5)';

  const handleTrackTutorialEnded = (tour: Driver, name: string) => {
    const steps = tour.getConfig()?.steps ?? [];
    const activeStep = tour.getActiveStep();

    const activeStepIndex =
      activeStep &&
      steps.findIndex(
        (step) =>
          step.element === activeStep.element &&
          step.popover?.title === activeStep.popover?.title,
      );

    const activeStepNumber =
      typeof activeStepIndex === 'number' ? activeStepIndex + 1 : undefined;

    tracking.ampli.tutorialEnded({
      ...getAmplitudeDefaultEventProperties(),
      name,
      stepNumber: activeStepNumber,
    });
  };

  const triggerNavigationTour = () => {
    const handleClose = async () => {
      handleTrackTutorialEnded(navigationTour, 'Navigation');
      await closeTourOrJumpToFinalStep({
        tour: navigationTour,
        autoTriggered,
        onTourComplete: async () => {
          if (shouldShowTour.newUser) {
            await markTutorialAsComplete({
              introducerId,
              tutorial: 'new_user',
            });
          }
        },
      });
    };

    const onNextClick = () => {
      navigationTour.moveNext();
    };

    const PROGRESS_STEP_COUNT = 2;
    const steps: DriveStep[] = [
      {
        element: '#nav-ctas-left',
        popover: {
          showProgress: true,
          progressText: renderProgressText(1, PROGRESS_STEP_COUNT),
          title: t`🚀 Welcome to the Portal`,
          description: t`<p>You can always access the <strong>Dashboard</strong> to manage applications, explore <strong>Products</strong> we offer, and use the <strong>Calculator</strong> for quick estimates.</p><p>Click <strong>“Create a new app”</strong> anytime to start a new application.</p>`,
          side: 'bottom',
          align: 'center',
          nextBtnText: nextText,
          onNextClick,
        },
        disableActiveInteraction: true,
      },
      {
        element: '#nav-ctas-right',
        popover: {
          showProgress: true,
          progressText: renderProgressText(2, PROGRESS_STEP_COUNT),
          title: t`💡 Helpful tools and support`,
          description: t`<p>Stay up to date with <strong>What's New</strong>, find quick answers in <strong>FAQs</strong>, or click your profile icon to invite colleagues and relaunch tours.</p><p>These tools are here to support you as you create applications.</p>`,
          nextBtnText: gotItText,
          side: 'bottom',
          align: 'center',
          onNextClick: autoTriggered ? onNextClick : handleClose,
        },
        disableActiveInteraction: true,
      },
    ];

    addRelaunchStepWhenApplicable(steps, autoTriggered, handleClose);

    const navigationTour = driver({
      showButtons: ['next', 'close'],
      popoverClass: 'driver-popover-custom',
      steps,
      onCloseClick: handleClose,
      overlayClickBehavior: undefined,
      stageRadius: 8,
      overlayColor: OVERLAY_COLOR,
    });

    navigationTour.drive();
  };

  const triggerCalculatorTour = () => {
    const onNextClick = () => {
      calculatorTour.moveNext();
    };

    const handleClose = async () => {
      handleTrackTutorialEnded(calculatorTour, 'Calculator');
      await closeTourOrJumpToFinalStep({
        tour: calculatorTour,
        autoTriggered,
        onTourComplete: async () => {
          if (shouldShowTour.calculator) {
            await markTutorialAsComplete({
              introducerId,
              tutorial: 'calculator',
            });
          } else {
            await markTutorialAsComplete({
              introducerId,
              tutorial: 'existing_introducer_opt_in',
            });
          }
        },
      });
    };

    const onCompleteTour = async () => {
      await handleClose();
      navigate(PATH_NAMES.calculator(), { replace: true });
    };

    const buildProgressText = (step: number) => {
      const PROGRESS_STEP_COUNT = 4;
      return renderProgressText(step, PROGRESS_STEP_COUNT);
    };

    const steps: DriveStep[] = [
      {
        element: '#term-lengths',
        popover: {
          showProgress: true,
          progressText: buildProgressText(1),
          title: t`⏳ Choose term length`,
          description: t`<p>Try different term lengths to see how they affect monthly and total repayments.</p>`,
          side: 'bottom',
          align: 'center',
          nextBtnText: nextText,
          onNextClick,
        },
        disableActiveInteraction: true,
      },
      {
        element: '#calculator-customisation',
        popover: {
          showProgress: true,
          progressText: buildProgressText(2),
          popoverClass: 'driver-popover-custom right-aligned',
          title: t`💵 Customise your loan`,
          description: t`<p>Enter loan amount, interest rate, and withdrawal fee - or use the sliders to test different scenarios.</p>`,
          nextBtnText: nextText,
          side: 'right',
          align: 'center',
          onNextClick,
        },
        disableActiveInteraction: true,
      },
      {
        element: '#repayment-schedule',
        popover: {
          showProgress: true,
          progressText: buildProgressText(3),
          popoverClass: 'driver-popover-custom left-aligned',
          title: t`📅 View repayment schedule`,
          description: t`<p>The repayment schedule updates automatically based on your chosen term length and loan details.</p><p>You can <strong>export it as a CSV file</strong> to share with clients.</p>`,
          nextBtnText: nextText,
          side: 'left',
          align: 'center',
          onNextClick,
        },
        disableActiveInteraction: true,
      },
      {
        element: '#overpayment-options',
        popover: {
          showProgress: true,
          progressText: buildProgressText(4),
          popoverClass: 'driver-popover-custom right-aligned',
          title: t`💡 Calculate early repayments`,
          description: t`<p>Tick <strong>“Calculate early repayments”</strong> to estimate repayments based on a set timeframe or amount.</p><p>The repayment schedule will update automatically.</p>`,
          nextBtnText: gotItText,
          side: 'right',
          align: 'center',
          onNextClick: autoTriggered ? onNextClick : onCompleteTour,
        },
        disableActiveInteraction: true,
      },
    ];

    addRelaunchStepWhenApplicable(steps, autoTriggered, onCompleteTour);

    const calculatorTour = driver({
      showButtons: ['next', 'close'],
      popoverClass: 'driver-popover-custom',
      steps,
      stageRadius: 8,
      onCloseClick: handleClose,
      overlayClickBehavior: undefined,
      overlayColor: OVERLAY_COLOR,
    });

    calculatorTour.drive();
  };

  const triggerDashboardTour = () => {
    const handleClose = async () => {
      handleTrackTutorialEnded(dashboardTour, 'Dashboard');
      cleanupRedDots();
      await closeTourOrJumpToFinalStep({
        tour: dashboardTour,
        autoTriggered,
        onTourComplete: async () => {
          if (shouldShowTour.firstApplication) {
            await markTutorialAsComplete({
              introducerId,
              tutorial: 'first_application',
            });
          } else {
            await markTutorialAsComplete({
              introducerId,
              tutorial: 'existing_introducer_opt_in',
            });
          }
        },
      });
    };

    const dragAndDropButtonElement = document.querySelector(
      '#status-column #drag-and-drop-button',
    );
    const filterIconElement = document.querySelector(
      '#termLength-column #column-filter-icon',
    );
    const sortIconElement = document.querySelector(
      '#approvalAmount-column #column-sort-icon',
    );
    const statusGuideElement = document.querySelector(
      '#status-column #status-guide',
    );

    const RED_DOT_CLASSES = {
      normal: ['highlight-red-dot'],
      central: ['highlight-red-dot', 'central-highlight'],
    } as const;

    const addRedDot = (
      element: Element | null,
      type: keyof typeof RED_DOT_CLASSES = 'normal',
    ) => element?.classList.add(...RED_DOT_CLASSES[type]);

    const removeRedDot = (
      element: Element | null,
      type: keyof typeof RED_DOT_CLASSES = 'normal',
    ) => element?.classList.remove(...RED_DOT_CLASSES[type]);

    const cleanupRedDots = () => {
      removeRedDot(filterIconElement);
      removeRedDot(sortIconElement);
      removeRedDot(dragAndDropButtonElement);
      removeRedDot(statusGuideElement, 'central');
    };

    const onCompleteTour = async () => {
      await handleClose();
      navigate(PATH_NAMES.home(), { replace: true });
    };

    const buildProgressText = (step: number) => {
      const PROGRESS_STEP_COUNT = 9;
      return renderProgressText(step, PROGRESS_STEP_COUNT);
    };

    const steps: DriveStep[] = [
      {
        element: '#quick-filters',
        popover: {
          showProgress: true,
          progressText: buildProgressText(1),
          title: t`⚡️ Quick filters`,
          description: t`<p>Move through the pipeline easily with grouped application statuses.</p>`,
          side: 'bottom',
          align: 'center',
          nextBtnText: nextText,
          onNextClick: () => {
            dashboardTour.moveNext();
          },
        },
        disableActiveInteraction: true,
      },
      {
        element: '#search-bar',
        popover: {
          showProgress: true,
          progressText: buildProgressText(2),
          popoverClass: 'driver-popover-custom',
          title: t`🔍 Search bar`,
          description: t`<p>Find specific applications by entering a <strong>company name</strong>, <strong>company number</strong>, or <strong>applicant name</strong>.</p>`,
          nextBtnText: nextText,
          side: 'bottom',
          align: 'center',
          onNextClick: () => {
            dashboardTour.moveNext();
          },
        },
        disableActiveInteraction: true,
      },
      {
        element: '#export-csv',
        popover: {
          showProgress: true,
          progressText: buildProgressText(3),
          popoverClass: 'driver-popover-custom',
          title: t`📤 Export as CSV`,
          description: t`<p>Download your application list as a <strong>CSV file</strong> to use in your own reports.</p>`,
          nextBtnText: nextText,
          side: 'bottom',
          align: 'end',
          onNextClick: () => {
            dashboardTour.moveNext();
          },
        },
        disableActiveInteraction: true,
      },
      {
        element: '#results-bar',
        popover: {
          showProgress: true,
          progressText: buildProgressText(4),
          popoverClass: 'driver-popover-custom',
          title: t`📊 Results bar`,
          description: t`<p>See how many applications you’re viewing and for which <strong>date range</strong>. After you filter, click <strong>“Clear filters”</strong> to see all applications again.</p>`,
          nextBtnText: nextText,
          side: 'bottom',
          align: 'start',
          onNextClick: () => {
            dashboardTour.moveNext();
          },
        },
        disableActiveInteraction: true,
      },
      {
        element: '#dashboard-settings',
        popover: {
          showProgress: true,
          progressText: buildProgressText(5),
          popoverClass: 'driver-popover-custom',
          title: t`⚙️ Dashboard settings`,
          description: t`<p>Customise your dashboard with a <strong>specific date range</strong> or <strong>columns</strong> to fit your workflow.</p>`,
          nextBtnText: nextText,
          side: 'bottom',
          align: 'end',
          onNextClick: () => {
            dashboardTour.moveNext();
          },
        },
        disableActiveInteraction: true,
      },
      {
        element: '#termLength-column',
        onHighlightStarted: () => addRedDot(filterIconElement),
        popover: {
          showProgress: true,
          progressText: buildProgressText(6),
          title: t`🪄 Filter by column`,
          description: t`<p>Click the <strong>filter icon</strong> next to a column header to add criteria and refine your application list.</p>`,
          nextBtnText: nextText,
          side: 'bottom',
          align: 'center',
          onNextClick: () => {
            dashboardTour.moveNext();
            removeRedDot(filterIconElement);
          },
        },
        disableActiveInteraction: true,
      },
      {
        element: '#approvalAmount-column',
        onHighlightStarted: () => addRedDot(sortIconElement),
        popover: {
          showProgress: true,
          progressText: buildProgressText(7),
          title: t`↕️ Sort by column`,
          description: t`<p>Click the <strong>sort icon</strong> next to a column header to sort your list in ascending or descending order.</p>`,
          nextBtnText: nextText,
          side: 'bottom',
          align: 'center',
          onNextClick: () => {
            dashboardTour.moveNext();
            removeRedDot(sortIconElement);
          },
        },
        disableActiveInteraction: true,
      },
      {
        element: '#status-column',
        onHighlightStarted: () => {
          // Driver.js does not fire onDeselected for the currently active step when navigating backward from the last step
          // this will be a tiny edge case where someone might navigate back using keyboard but added for extra safety
          removeRedDot(statusGuideElement, 'central');
          addRedDot(dragAndDropButtonElement);
        },
        popover: {
          showProgress: true,
          progressText: buildProgressText(8),
          title: t`✋ Drag & drop`,
          description: t`<p>Hover over a column header and drag it to rearrange columns and personalise your view.</p>`,
          nextBtnText: nextText,
          side: 'bottom',
          align: 'center',
          onNextClick: () => {
            dashboardTour.moveNext();
            removeRedDot(dragAndDropButtonElement);
          },
        },
        disableActiveInteraction: true,
      },
      {
        element: '#status-column',
        onHighlightStarted: () => addRedDot(statusGuideElement, 'central'),
        popover: {
          showProgress: true,
          progressText: buildProgressText(9),
          title: t`📘 View status guide`,
          description: t`<p>Click <strong>“Application status”</strong> to open the status guide explaining what each status means.</p>`,
          nextBtnText: gotItText,
          side: 'bottom',
          align: 'center',
          onNextClick: autoTriggered
            ? () => {
                dashboardTour.moveNext();
                removeRedDot(statusGuideElement, 'central');
              }
            : onCompleteTour,
        },
        disableActiveInteraction: true,
      },
    ];

    addRelaunchStepWhenApplicable(steps, autoTriggered, onCompleteTour);

    const dashboardTour = driver({
      showButtons: ['next', 'close'],
      popoverClass: 'driver-popover-custom',
      steps,
      overlayClickBehavior: undefined,
      overlayColor: OVERLAY_COLOR,
      onCloseClick: handleClose,
      // ensure red dots are removed when closing the tour at any step
      onDeselected: cleanupRedDots,
    });

    dashboardTour.drive();
  };

  const tutorialCompletion = useMemo(() => {
    const defaultTutorialValues: Record<TTutorialName, boolean> = {
      new_user: true,
      calculator: true,
      first_application: true,
      existing_introducer_opt_in: true,
    };

    if (!tutorialsData?.data) return defaultTutorialValues;

    return tutorialsData.data.reduce((acc, tutorialData) => {
      acc[tutorialData.tutorial] = Boolean(tutorialData.completed_at);
      return acc;
    }, defaultTutorialValues);
  }, [tutorialsData]);

  const shouldShowTour = useMemo(
    () => ({
      newUser: !tutorialCompletion['new_user'],
      calculator: !tutorialCompletion['calculator'],
      firstApplication: !tutorialCompletion['first_application'],
      existingIntroducerOptIn:
        !tutorialCompletion['existing_introducer_opt_in'],
    }),
    [tutorialCompletion],
  );

  return {
    shouldShowTour,
    triggerNavigationTour,
    triggerDashboardTour,
    triggerCalculatorTour,
  };
};
